import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useCallback, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl-next';
import uuid from 'uuid/v4';
import { doc, p } from '@atlaskit/adf-utils/builders';
import AiAgentIcon from '@atlaskit/icon/core/ai-agent';
import MoreIcon from '@atlaskit/icon/glyph/more';
import { fg } from '@atlaskit/platform-feature-flags';
import { AgentAvatar } from '@atlaskit/rovo-agent-components';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/experiments';
import { RovoAgentIcon } from '@atlassian/conversation-assistant-ui-components';
import { UserInputCommandPalette } from '@atlassian/generative-ai-modal/screens/UserInputCommandPalette';
import { useAnalyticsFlowEventsQueue } from '../../analytics/analytics-flow/analyticsFlowUtils';
import { getTranslatedItemTitle, getTranslatedPromptLabel } from '../../config-items/config-items';
import { openBrowseAgentsModal } from '../../pm-plugins/rovo-agents/commands';
import { useAgents } from '../../pm-plugins/rovo-agents/useAgents';
import { createAgentConfigItem } from '../../prebuilt/config-item-agents/create-agent-config-item';
import { PreloadModalComponent } from '../../ui/components/BrowseRovoAgentsModal/BrowseRovoAgentsModal';
import { BROWSE_AGENTS, USE_GENERAL_AI_KNOWLEDGE, USE_ROVO_ORGANISATION_KNOWLEDGE } from '../../utils/constants';
import { messages } from '../messages';
import { useAIExperienceCommonDataContext } from '../useAIExperienceCommonData';
import { useCommandPaletteStateMachine } from '../useCommandPaletteStateMachine';
import { useOutsideClickHandler } from '../useOutsideClickHandler';

/**
 * This component is responsible for the logic and rendering of the
 * Command Palette component, it also handles the corresponding state machine.
 */
export function UserInputCommandPaletteWithLogic(_ref) {
  var experienceAppContext = _ref.context,
    initialPrompt = _ref.initialPrompt;
  var aiExperienceCommonData = useAIExperienceCommonDataContext();
  var appearance = aiExperienceCommonData.appearance,
    editorPluginAIProvider = aiExperienceCommonData.editorPluginAIProvider,
    endExperience = aiExperienceCommonData.endExperience,
    sendToAIExperienceMachine = aiExperienceCommonData.sendToAIExperienceMachine,
    modalRef = aiExperienceCommonData.modalRef;
  var _useIntl = useIntl(),
    formatMessage = _useIntl.formatMessage;
  var startingConfig = experienceAppContext.configItem,
    baseGenerate = experienceAppContext.baseGenerate,
    startingUserInput = experienceAppContext.userInput,
    startingUserADFInput = experienceAppContext.userADFInput,
    parentPresetPromptLabel = experienceAppContext.parentPresetPromptLabel,
    editCount = experienceAppContext.editCount,
    positions = experienceAppContext.positions,
    editorView = experienceAppContext.editorView,
    latestPromptResponse = experienceAppContext.latestPromptResponse,
    aiProvider = experienceAppContext.aiProvider;
  var _React$useState = React.useState(false),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    initialPromptTriggered = _React$useState2[0],
    setInitialPromptTriggered = _React$useState2[1];

  // if the user selects a submenu via a parent (`Change tone` -> `Neutral`)
  // we want to use startingConfig and parentPresetPromptLabel to restore the selected parent
  var _useCommandPaletteSta = useCommandPaletteStateMachine({
      baseGenerate: baseGenerate,
      startingConfig: startingConfig,
      startingPresetPromptLabel: parentPresetPromptLabel || getTranslatedPromptLabel(startingConfig, formatMessage),
      startingUserInput: startingUserInput,
      startingUserADFInput: startingUserADFInput
    }),
    commandPaletteContext = _useCommandPaletteSta.context,
    sendToCommandPalette = _useCommandPaletteSta.send;
  var textInput = commandPaletteContext.textInput,
    adfInput = commandPaletteContext.adfInput,
    configItem = commandPaletteContext.configItem,
    presetPromptLabel = commandPaletteContext.presetPromptLabel;

  // if the user selects a parent with nesting submenus (`Change tone` or `Translate`)
  var isParentWithNestedSubmenusSelected = Boolean(
  // editorExperiment should be first, because it emits exposure event
  editorExperiment('platform_editor_ai_command_palate_improvement', 'test', {
    exposure: true
  }) && presetPromptLabel && configItem.nestingConfig);
  editorExperiment('platform_editor_ai_command_palette_post_ga', 'test', {
    exposure: true
  });
  useOutsideClickHandler({
    modalRef: modalRef,
    onCancel: function onCancel() {
      return aiExperienceCommonData.endExperience();
    },
    onDiscard: function onDiscard() {
      sendToAIExperienceMachine({
        type: 'update context',
        context: {
          configItem: configItem,
          userInput: textInput || '',
          userADFInput: adfInput
        }
      });
      sendToAIExperienceMachine({
        type: 'enable discard'
      });
    }
  });
  var focusInputRef = React.useRef(undefined);
  var handleCancel = function handleCancel() {
    endExperience();
  };
  var kickoffAiExperience = useCallback(function (_ref2) {
    var configItem = _ref2.configItem,
      userInput = _ref2.userInput,
      userADFInput = _ref2.userADFInput;
    // if the user selects a parent with nesting submenus (`Change tone` or `Translate`)
    // we need the parentPresetPromptLabel to restore the selected parent later when we edit prompt in `Preview` screen (onEditPromptClick)
    var parentPresetPromptLabel = isParentWithNestedSubmenusSelected ? presetPromptLabel : '';
    sendToAIExperienceMachine({
      type: 'kickoff with command palette input',
      context: {
        parentPresetPromptLabel: parentPresetPromptLabel,
        configItem: configItem,
        userInput: userInput,
        userADFInput: userADFInput
      }
    });
  }, [sendToAIExperienceMachine, isParentWithNestedSubmenusSelected, presetPromptLabel]);
  var onPartialPresetSelected = useCallback(function (configItem) {
    var _focusInputRef$curren;
    var _ref3 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
      presetPromptLabel = _ref3.presetPromptLabel;
    // If partial prompt is selected we want to update the command palette
    // And focus the text input
    sendToCommandPalette({
      type: 'onPartialPresetSelected',
      configItem: configItem,
      presetPromptLabel: presetPromptLabel !== null && presetPromptLabel !== void 0 ? presetPromptLabel : getTranslatedPromptLabel(configItem, formatMessage)
    });
    (_focusInputRef$curren = focusInputRef.current) === null || _focusInputRef$curren === void 0 || _focusInputRef$curren.call(focusInputRef);
  }, [sendToCommandPalette, formatMessage]);
  var getPromptTitle = useCallback(function (configItem) {
    var _configItem$agent, _configItem$agent2;
    if (((_configItem$agent = configItem.agent) === null || _configItem$agent === void 0 ? void 0 : _configItem$agent.creatorType) === 'CUSTOMER') {
      return undefined;
    }
    if (((_configItem$agent2 = configItem.agent) === null || _configItem$agent2 === void 0 ? void 0 : _configItem$agent2.creatorType) === 'SYSTEM') {
      var _configItem$agent3;
      return (_configItem$agent3 = configItem.agent) === null || _configItem$agent3 === void 0 ? void 0 : _configItem$agent3.name;
    }
    return configItem.promptLabel ? formatMessage(configItem.promptLabel) : undefined;
  }, [formatMessage]);
  var analyticsFlow = useAnalyticsFlowEventsQueue({
    stepName: 'prompt',
    attributes: {
      experienceName: configItem.key,
      promptTitle: getPromptTitle(configItem),
      // !TODO AIFOLLOWUP - Remove this
      // This attribute only made sense when we had two different entry points
      // 'custom' or 'generate'
      // This should likely change to respresent the actual prompt types:
      // 'full-prompt' = when there is no user input (e.g. 'Summarize this page')
      // 'partial-prompt' = when there is user input and a prompt (e.g. 'Brainstorm: "Titles for this page"')
      // 'generate' = there is only user input, and no preset prompt
      promptType: 'generate',
      usingCommandPalette: true,
      editCount: editCount
    }
  });
  var updateAnalytics = useCallback(function (configItem) {
    var _configItem$getBacken;
    /**
     * Update the backendModel common attribute whenever the configItem changes
     */
    analyticsFlow.updateCommonAttributes(_objectSpread({
      backendModel: configItem === null || configItem === void 0 || (_configItem$getBacken = configItem.getBackendModel) === null || _configItem$getBacken === void 0 ? void 0 : _configItem$getBacken.call(configItem, !!latestPromptResponse)
    }, analyticsFlow.getUserFlowCommonAttributes(configItem)));
    // update experiencename and title as it changes on selecting suggestion
    analyticsFlow.addAttributes({
      experienceName: configItem.key,
      promptTitle: getPromptTitle(configItem)
    });
  }, [analyticsFlow, latestPromptResponse, getPromptTitle]);
  var rovoAgentsPluginState = useAgents({
    editorState: editorView.state
  });
  var updateTextInput = useCallback(function (textInputValue) {
    sendToCommandPalette({
      type: 'onTextEntered',
      input: textInputValue
    });
  }, [sendToCommandPalette]);
  var updateADFInput = React.useCallback(function (text) {
    sendToCommandPalette({
      type: 'onADFEntered',
      adf: text
    });
  }, [sendToCommandPalette]);
  var onSubmit = useCallback(function () {
    var _ref4 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      _configItem = _ref4.configItem;
    // if the user selects a parent with nesting submenus (`Change tone` or `Translate`)
    // they can only trigger the AI experience by selecting a submenu (which can be triggered by pressing enter)
    if (isParentWithNestedSubmenusSelected) {
      return;
    }
    kickoffAiExperience({
      configItem: _configItem || commandPaletteContext.configItem,
      userInput: commandPaletteContext.textInput || '',
      userADFInput: commandPaletteContext.adfInput
    });
  }, [isParentWithNestedSubmenusSelected, kickoffAiExperience, commandPaletteContext.configItem, commandPaletteContext.textInput, commandPaletteContext.adfInput]);
  var __getSuggestions = useMemo(function () {
    return configItem.selectionType === 'range' ? editorPluginAIProvider.rangeConfig.getSuggestions : editorPluginAIProvider.emptyConfig.getSuggestions;
  }, [configItem.selectionType, editorPluginAIProvider.emptyConfig.getSuggestions, editorPluginAIProvider.rangeConfig.getSuggestions]);

  /**
   * This function is responsible for highlighting the entire document
   * when particular prompts are selected.
   *
   * This only happens if the user has not already made a selection.
   *
   * Currently all prompts highlight the whole document except partial prompts like:
   * - Brainstorm
   * - Ask Search
   */
  var handleHighlight = useCallback(function (_ref5) {
    var configItem = _ref5.configItem,
      promptLabel = _ref5.promptLabel;
    if (editorExperiment('platform_editor_ai_command_palate_improvement', 'test')) {
      // Remove all decorations if the prompt is 'Tell AI' or that it is not defined (brainstorm)
      if (configItem.promptLabel !== undefined && !configItem.agent || promptLabel === 'tell-ai' || promptLabel === USE_GENERAL_AI_KNOWLEDGE || promptLabel === USE_ROVO_ORGANISATION_KNOWLEDGE || promptLabel === BROWSE_AGENTS && editorExperiment('platform_editor_ai_command_palette_post_ga', 'test')) {
        var removeTransaction = editorView.state.tr;
        removeTransaction.setMeta('hideSelectionPreview', true);
        editorView.dispatch(removeTransaction);
        return;
      }
    } else {
      // Remove all decorations if the prompt is 'Tell AI' or that it is not defined (brainstorm)
      if (configItem.promptLabel !== undefined || promptLabel === 'tell-ai') {
        var _removeTransaction = editorView.state.tr;
        _removeTransaction.setMeta('hideSelectionPreview', true);
        editorView.dispatch(_removeTransaction);
        return;
      }
    }

    // Highlight the entire document for all other prompts
    var setTransaction = editorView.state.tr;
    setTransaction.setMeta('showSelectionPreview', true);
    editorView.dispatch(setTransaction);
  }, [editorView]);
  var onConverSationStarterSelected = useCallback(function (conversationStarter) {
    var userADFInput = doc(p(conversationStarter));
    handleHighlight({
      configItem: configItem
    });
    updateAnalytics(configItem);
    onPartialPresetSelected(configItem, editorExperiment('platform_editor_ai_command_palate_improvement', 'test') ? {
      presetPromptLabel: "@".concat(configItem.agent.name)
    } : undefined);
    kickoffAiExperience({
      userInput: conversationStarter,
      configItem: configItem,
      userADFInput: userADFInput
    });
  }, [handleHighlight, updateAnalytics, onPartialPresetSelected, kickoffAiExperience, configItem]);
  useEffect(function () {
    if (initialPrompt && !initialPromptTriggered) {
      if (configItem.agent) {
        onConverSationStarterSelected(initialPrompt);
      } else {
        if (!experienceAppContext.latestPrompt) {
          var userADFInput = doc(p(initialPrompt));
          updateADFInput(userADFInput);
          updateTextInput(initialPrompt);
          setInitialPromptTriggered(true);
          kickoffAiExperience({
            userInput: initialPrompt,
            configItem: configItem,
            userADFInput: userADFInput
          });
        }
      }
      setInitialPromptTriggered(true);
    }
  }, [initialPrompt, onConverSationStarterSelected, initialPromptTriggered, configItem, experienceAppContext.latestPrompt, updateADFInput, updateTextInput, kickoffAiExperience]);
  var getSuggestions = useCallback(function (inputValue, agent) {
    // if like Brainstorm, return nothing
    if (presetPromptLabel && !configItem.nestingConfig && !agent) {
      return [];
    }

    /**
     * knowledge source
     */

    function makeUseRovoOrganisationKnowledgeSuggestion(defaultAgent) {
      var agentConfigItem = createAgentConfigItem(startingConfig.selectionType, defaultAgent, editorPluginAIProvider);
      return {
        testId: USE_ROVO_ORGANISATION_KNOWLEDGE,
        value: USE_ROVO_ORGANISATION_KNOWLEDGE,
        label: formatMessage(messages.useRovoOrganisationKnowledgeLabel),
        groupHeading: formatMessage(messages.knowledgeSourceGroupHeading),
        rightText: formatMessage(messages.useRovoOrganisationKnowledgeRightText),
        hideIcon: true,
        onSelect: function onSelect() {
          handleHighlight({
            configItem: agentConfigItem
          });
          updateAnalytics(agentConfigItem);
          onSubmit({
            configItem: agentConfigItem
          });
        },
        onSuggestionFocused: function onSuggestionFocused() {
          handleHighlight({
            configItem: agentConfigItem,
            promptLabel: USE_ROVO_ORGANISATION_KNOWLEDGE
          });
        }
      };
    }
    function makeKnowledgeSourceSuggestions() {
      var useRovoOrganisationKnowledgeSuggestions = !(rovoAgentsPluginState !== null && rovoAgentsPluginState !== void 0 && rovoAgentsPluginState.enabled) || !(rovoAgentsPluginState !== null && rovoAgentsPluginState !== void 0 && rovoAgentsPluginState.defaultAgent) || !aiProvider.getChannelVariables || agent ? [] :
      // pass defaultAgent to avoid ts check again
      [makeUseRovoOrganisationKnowledgeSuggestion(rovoAgentsPluginState.defaultAgent)];
      var knowledgeSourceSuggestions = [{
        testId: USE_GENERAL_AI_KNOWLEDGE,
        value: USE_GENERAL_AI_KNOWLEDGE,
        label: formatMessage(messages.useGeneralAiKnowledgeLabel),
        groupHeading: formatMessage(messages.knowledgeSourceGroupHeading),
        rightText: formatMessage(messages.useGeneralAiKnowledgeRightText),
        hideIcon: true,
        onSelect: function onSelect() {
          handleHighlight({
            configItem: configItem
          });
          updateAnalytics(configItem);
          onSubmit();
        },
        onSuggestionFocused: function onSuggestionFocused() {
          handleHighlight({
            configItem: configItem,
            promptLabel: USE_GENERAL_AI_KNOWLEDGE
          });
        }
      }].concat(useRovoOrganisationKnowledgeSuggestions);
      return knowledgeSourceSuggestions;
    }

    // use this flag to determine if we should show the knowledge source suggestions instead of the 'Tell AI' suggestion
    var showKnowledgeSourceSuggestionsInsteadOfTellAI = (rovoAgentsPluginState === null || rovoAgentsPluginState === void 0 ? void 0 : rovoAgentsPluginState.enabled) && (rovoAgentsPluginState === null || rovoAgentsPluginState === void 0 ? void 0 : rovoAgentsPluginState.defaultAgent) && aiProvider.getChannelVariables && !agent && !presetPromptLabel && editorExperiment('platform_editor_ai_command_palate_improvement', 'test');

    /**
     * do not show tell-ai
     * - if there is no user input
     * - if the user selects a parent with nesting submenus (`Change tone` or `Translate`)
     *   - they can only trigger the AI experience by selecting a submenu (which can be triggered by pressing enter)
     */
    var tellAiSuggestion = !inputValue || isParentWithNestedSubmenusSelected ? [] : showKnowledgeSourceSuggestionsInsteadOfTellAI ? makeKnowledgeSourceSuggestions() : [{
      value: agent ? 'tell-agent' : 'tell-ai',
      label: inputValue,
      hideIcon: true,
      onSelect: function onSelect() {
        handleHighlight({
          configItem: configItem
        });
        updateAnalytics(configItem);
        onSubmit();
      },
      onSuggestionFocused: function onSuggestionFocused() {
        handleHighlight({
          configItem: configItem,
          promptLabel: agent ? 'tell-agent' : 'tell-ai'
        });
      }
    }];
    var showMoreText = formatMessage(messages.showMoreButton);
    var showMoreSuggestion = editorExperiment('platform_editor_ai_command_palate_improvement', 'test') ? [{
      value: 'show-more',
      label: showMoreText,
      icon: /*#__PURE__*/React.createElement(MoreIcon, {
        label: showMoreText,
        primaryColor: "var(--ds-icon, #44546F)"
      }),
      onSelect: function onSelect() {
        handleHighlight({
          configItem: configItem
        });
        updateAnalytics(configItem);
      },
      onSuggestionFocused: function onSuggestionFocused() {
        handleHighlight({
          configItem: configItem,
          promptLabel: showMoreText
        });
      }
    }] : [];
    // The browse agents option is only available when using the new <BrowseAgentsModal>
    // component as we need to use its state to send the selected agent to the command palette
    var browseAgentsSuggestion = !(rovoAgentsPluginState !== null && rovoAgentsPluginState !== void 0 && rovoAgentsPluginState.enabled) ||
    // eslint-disable-next-line @atlaskit/platform/ensure-feature-flag-prefix
    !fg('rovo-chat-browse-agents') || !aiProvider.getChannelVariables || agent ? [] : {
      value: BROWSE_AGENTS,
      label: formatMessage(messages.agentsBrowseMore),
      icon: editorExperiment('platform_editor_ai_command_palette_post_ga', 'test') ? /*#__PURE__*/React.createElement(AiAgentIcon, {
        label: "browse-more-agents",
        color: "var(--ds-icon, #44546F)"
      }) : /*#__PURE__*/React.createElement(RovoAgentIcon, {
        label: "browse-more-agents"
      }),
      groupHeading: editorExperiment('platform_editor_ai_command_palette_post_ga', 'test') ? formatMessage(messages.starredAgentsSuggestionGroupHeading, {
        numberOfStarredAgents: "".concat(rovoAgentsPluginState === null || rovoAgentsPluginState === void 0 ? void 0 : rovoAgentsPluginState.agents.length)
      }) : formatMessage(messages.agentsSuggestionGroupHeading),
      onSelect: function onSelect() {
        if (editorExperiment('platform_editor_ai_command_palate_improvement', 'test')) {
          openBrowseAgentsModal(true, positions)(editorView.state, editorView.dispatch);
        } else {
          openBrowseAgentsModal(true)(editorView.state, editorView.dispatch);
        }
      },
      onSuggestionFocused: function onSuggestionFocused() {
        handleHighlight({
          configItem: configItem,
          promptLabel: BROWSE_AGENTS
        });
      }
    };

    /**
     * If an agent is selected, we want to show the agents conversation starters
     * as suggestions in the command palette with the 'Tell Agent' suggestion
     * when there is user input
     */
    if (agent) {
      var conversationStarterSuggestions = agent.conversationStarters.map(function (conversationStarter) {
        var _agent$externalConfig;
        var translatedMessage = typeof conversationStarter === 'string' ? conversationStarter : formatMessage(conversationStarter);
        return {
          value: "agent:".concat(agent.id, ":").concat(uuid(), ":").concat(translatedMessage),
          label: translatedMessage,
          icon: editorExperiment('platform_editor_ai_command_palate_improvement', 'test') ? /*#__PURE__*/React.createElement(AgentAvatar, {
            agentId: agent.id,
            agentNamedId: (_agent$externalConfig = agent.externalConfigReference) !== null && _agent$externalConfig !== void 0 ? _agent$externalConfig : agent.namedId,
            label: translatedMessage,
            agentIdentityAccountId: agent.identityAccountId,
            isForgeAgent: agent.creatorType === 'FORGE',
            forgeAgentIconUrl: agent.icon
          }) : /*#__PURE__*/React.createElement(RovoAgentIcon, {
            label: translatedMessage
          }),
          groupHeading: editorExperiment('platform_editor_ai_command_palette_post_ga', 'test') ? formatMessage(messages.starredAgentsSuggestionGroupHeading, {
            numberOfStarredAgents: "".concat(rovoAgentsPluginState === null || rovoAgentsPluginState === void 0 ? void 0 : rovoAgentsPluginState.agents.length)
          }) : formatMessage(messages.agentsSuggestionGroupHeading),
          onSelect: function onSelect() {
            onConverSationStarterSelected(translatedMessage);
          },
          onSuggestionFocused: function onSuggestionFocused() {
            handleHighlight({
              configItem: configItem
            });
          }
        };
      }).filter(function (configItem) {
        return configItem.label.toLowerCase().includes((inputValue || '').toLowerCase());
      });
      if (editorExperiment('platform_editor_ai_command_palette_post_ga', 'test')) {
        return conversationStarterSuggestions;
      }
      return [].concat(_toConsumableArray(conversationStarterSuggestions), _toConsumableArray(tellAiSuggestion));
    }

    /**
     * If an agent is not selected, we want to show the suggestions
     */
    var output = __getSuggestions({
      inputValue: inputValue
    }).filter(function (configItem) {
      return configItem.isVisible({
        editorView: editorView,
        positions: positions
      });
    }).map(function (configItem) {
      var _configItem$icon;
      var icon = (_configItem$icon = configItem.icon) === null || _configItem$icon === void 0 ? void 0 : _configItem$icon.call(configItem, {
        formatMessage: formatMessage,
        shownAt: 'suggestions'
      });

      // Show the icon for the parent config item if it exists
      if (configItem.nestingConfig && configItem.nestingConfig.icon && !presetPromptLabel && !inputValue && editorExperiment('platform_editor_ai_command_palate_improvement', 'test')) {
        var _configItem$nestingCo, _configItem$nestingCo2;
        icon = (_configItem$nestingCo = (_configItem$nestingCo2 = configItem.nestingConfig).icon) === null || _configItem$nestingCo === void 0 ? void 0 : _configItem$nestingCo.call(_configItem$nestingCo2, {
          formatMessage: formatMessage,
          shownAt: 'suggestions'
        });
      }
      return {
        icon: icon,
        label: configItem.nestingConfig && presetPromptLabel && editorExperiment('platform_editor_ai_command_palate_improvement', 'test') ? formatMessage(configItem.nestingConfig.shortTitle) : getTranslatedItemTitle(configItem, formatMessage),
        statusLozengeType: configItem.statusLozengeType,
        value: configItem.key,
        nestingConfig: configItem.nestingConfig && editorExperiment('platform_editor_ai_command_palate_improvement', 'test') ? {
          parentTestId: configItem.nestingConfig.parentTestId,
          parentTitle: formatMessage(configItem.nestingConfig.parentTitle)
        } : undefined,
        onSuggestionFocused: function onSuggestionFocused() {
          handleHighlight({
            configItem: configItem
          });
        },
        onSelect: function onSelect() {
          if (configItem.onSelectForPrompt) {
            configItem.onSelectForPrompt({
              context: experienceAppContext,
              editorView: editorView,
              endExperience: endExperience
            });
          }

          // A config item nested parent will have matching config but no prompt label
          // will be selected
          if (configItem.nestingConfig && !presetPromptLabel && !inputValue && editorExperiment('platform_editor_ai_command_palate_improvement', 'test')) {
            onPartialPresetSelected(configItem, {
              presetPromptLabel: formatMessage(configItem.nestingConfig.parentTitle)
            });
          } else {
            handleHighlight({
              configItem: configItem
            });
            updateAnalytics(configItem);

            // If full prompt is selected we want to kickoff the AI experience
            if (!configItem.promptLabel) {
              kickoffAiExperience({
                userInput: '',
                userADFInput: undefined,
                configItem: configItem
              });
            } else {
              onPartialPresetSelected(configItem);
            }
          }
        }
      };
    }).concat(showMoreSuggestion).concat(!agent ? tellAiSuggestion : [])
    /**
     * Only show the agents if the user hasn't already selected an agent
     */.concat(!(rovoAgentsPluginState !== null && rovoAgentsPluginState !== void 0 && rovoAgentsPluginState.enabled) || !aiProvider.getChannelVariables || agent ? [] : rovoAgentsPluginState.agents.map(function (agent) {
      var _agent$description, _agent$externalConfig2;
      var agentConfigItem = createAgentConfigItem(startingConfig.selectionType, agent, editorPluginAIProvider);
      return {
        value: "agent:".concat(agent.id),
        label: agent.name,
        description: (_agent$description = agent.description) !== null && _agent$description !== void 0 ? _agent$description : '',
        icon: /*#__PURE__*/React.createElement(React.Fragment, null, editorPluginAIProvider.isRovoEnabled && /*#__PURE__*/React.createElement(PreloadModalComponent, null), editorExperiment('platform_editor_ai_command_palate_improvement', 'test') ? /*#__PURE__*/React.createElement(AgentAvatar, {
          agentId: agent.id,
          agentNamedId: (_agent$externalConfig2 = agent.externalConfigReference) !== null && _agent$externalConfig2 !== void 0 ? _agent$externalConfig2 : agent.namedId,
          label: agent.name,
          agentIdentityAccountId: agent.identityAccountId,
          isForgeAgent: agent.creatorType === 'FORGE',
          forgeAgentIconUrl: agent.icon
        }) : /*#__PURE__*/React.createElement(RovoAgentIcon, {
          label: agent.name
        })),
        groupHeading: editorExperiment('platform_editor_ai_command_palette_post_ga', 'test') ? formatMessage(messages.starredAgentsSuggestionGroupHeading, {
          numberOfStarredAgents: "".concat(rovoAgentsPluginState === null || rovoAgentsPluginState === void 0 ? void 0 : rovoAgentsPluginState.agents.length)
        }) : formatMessage(messages.agentsSuggestionGroupHeading),
        isAgent: true,
        onSelect: function onSelect() {
          handleHighlight({
            configItem: agentConfigItem
          });
          updateAnalytics(agentConfigItem);
          onPartialPresetSelected(agentConfigItem, editorExperiment('platform_editor_ai_command_palate_improvement', 'test') ? {
            presetPromptLabel: "@".concat(agentConfigItem.agent.name)
          } : undefined);
        },
        onSuggestionFocused: function onSuggestionFocused() {
          handleHighlight({
            configItem: agentConfigItem
          });
        }
      };
    })).concat(editorExperiment('platform_editor_ai_command_palette_post_ga', 'test') ? [] : browseAgentsSuggestion).concat(agent ? tellAiSuggestion : []).filter(function (suggestion) {
      var _suggestion$nestingCo;
      if (!suggestion) {
        return false;
      }

      // Since we move `.concat(tellAiSuggestion)` above, we need to update the filter logic to include these suggestions
      if (suggestion.value === USE_GENERAL_AI_KNOWLEDGE || suggestion.value === USE_ROVO_ORGANISATION_KNOWLEDGE) {
        return true;
      }

      // if the user selects a parent with nesting submenus (`Change tone` or `Translate`)
      // ignore those that don't belong to the parent
      if (isParentWithNestedSubmenusSelected && ((_suggestion$nestingCo = suggestion.nestingConfig) === null || _suggestion$nestingCo === void 0 ? void 0 : _suggestion$nestingCo.parentTitle) !== presetPromptLabel) {
        return false;
      }

      // text filter
      var searchValues = [suggestion.label.toLowerCase()];
      if (suggestion !== null && suggestion !== void 0 && suggestion.isAgent) {
        searchValues.push('rovo', 'agent');
      }
      return searchValues.join(' ').includes((inputValue || '').toLowerCase());
    }).concat(editorExperiment('platform_editor_ai_command_palette_post_ga', 'test') && !isParentWithNestedSubmenusSelected ? browseAgentsSuggestion : []).map(function (suggestion) {
      if (!suggestion.groupHeading && editorExperiment('platform_editor_ai_command_palate_improvement', 'test')) {
        var _configItem$nestingCo3;
        // If the suggestion is in a submenu, we want to show the parent title as the group heading
        suggestion.groupHeading = editorExperiment('platform_editor_ai_command_palette_post_ga', 'test') && isParentWithNestedSubmenusSelected && configItem !== null && configItem !== void 0 && (_configItem$nestingCo3 = configItem.nestingConfig) !== null && _configItem$nestingCo3 !== void 0 && _configItem$nestingCo3.parentTitle ? formatMessage(configItem.nestingConfig.parentTitle) : formatMessage(messages.atlassianIntelligenceGroupHeading);
      }
      return suggestion;
    });
    if (output.length) {
      return output;
    }
    return isParentWithNestedSubmenusSelected && editorExperiment('platform_editor_ai_command_palate_improvement', 'test') ? [{
      value: 'no-suggestions',
      label: formatMessage(messages.noResultsSuggestion),
      hideIcon: true,
      icon: undefined,
      onSelect: function onSelect() {},
      onSuggestionFocused: function onSuggestionFocused() {}
    }] : [];
  }, [presetPromptLabel, configItem, isParentWithNestedSubmenusSelected, formatMessage, __getSuggestions, rovoAgentsPluginState === null || rovoAgentsPluginState === void 0 ? void 0 : rovoAgentsPluginState.enabled, rovoAgentsPluginState === null || rovoAgentsPluginState === void 0 ? void 0 : rovoAgentsPluginState.agents, rovoAgentsPluginState === null || rovoAgentsPluginState === void 0 ? void 0 : rovoAgentsPluginState.defaultAgent, aiProvider.getChannelVariables, handleHighlight, updateAnalytics, onSubmit, onConverSationStarterSelected, editorView, positions, experienceAppContext, endExperience, onPartialPresetSelected, kickoffAiExperience, startingConfig.selectionType, editorPluginAIProvider]);
  var removeRefinementTag = useCallback(function () {
    var _baseGenerate$getBack, _focusInputRef$curren2;
    // Set experienceName and promptTitle to default values
    analyticsFlow.addAttributes({
      experienceName: baseGenerate === null || baseGenerate === void 0 ? void 0 : baseGenerate.key,
      promptTitle: baseGenerate ? getTranslatedPromptLabel(baseGenerate, formatMessage) : ''
    });
    analyticsFlow.updateCommonAttributes({
      backendModel: baseGenerate === null || baseGenerate === void 0 || (_baseGenerate$getBack = baseGenerate.getBackendModel) === null || _baseGenerate$getBack === void 0 ? void 0 : _baseGenerate$getBack.call(baseGenerate, !!latestPromptResponse)
    });
    sendToCommandPalette({
      type: 'onPartialPresetRemoved'
    });
    (_focusInputRef$curren2 = focusInputRef.current) === null || _focusInputRef$curren2 === void 0 || _focusInputRef$curren2.call(focusInputRef);
  }, [analyticsFlow, baseGenerate, formatMessage, latestPromptResponse, sendToCommandPalette]);
  var onBack = useCallback(function () {
    removeRefinementTag();
    updateTextInput('');
    updateADFInput();
  }, [removeRefinementTag, updateTextInput, updateADFInput]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(UserInputCommandPalette, {
    appearance: appearance,
    presetPromptLabel: presetPromptLabel,
    inputValue: textInput || '',
    inputADFValue: adfInput,
    onInputUpdate: updateTextInput,
    onADFUpdate: updateADFInput,
    onCancel: handleCancel,
    onEscapeKeyPressed: handleCancel,
    onSubmit: onSubmit,
    onRefinementTagRemove: removeRefinementTag,
    onBack: onBack,
    placeholder: configItem.promptHint ? formatMessage(configItem.promptHint) : '',
    getSuggestions: getSuggestions,
    focusInputRef: focusInputRef,
    PromptEditor: editorPluginAIProvider.PromptEditor,
    agent: configItem.agent,
    hideLegacySubmitCancelButtons: isParentWithNestedSubmenusSelected
  }));
}